import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkle, ArrowRight } from "@phosphor-icons/react";

const fmt$ = (n) => "$" + Number(n || 0).toLocaleString();

export default function ProposalDialog({ opportunity, open, onOpenChange, onPromoted }) {
  const [extra, setExtra] = useState("");
  const [proposal, setProposal] = useState(opportunity?.proposal_draft || null);
  const [busy, setBusy] = useState(false);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => { setProposal(opportunity?.proposal_draft || null); setExtra(""); }, [opportunity?.id]);

  if (!opportunity) return null;

  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/opportunities/${opportunity.id}/generate-proposal`, { opportunity_id: opportunity.id, extra_context: extra });
      setProposal(data.proposal);
      toast.success("Proposal drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const promote = async () => {
    if (!opportunity.client_id) return toast.error("Link this opportunity to a client first");
    setPromoting(true);
    try {
      const { data } = await api.post(`/opportunities/${opportunity.id}/promote-to-engagement`);
      toast.success("Engagement created & opportunity marked Won");
      onOpenChange(false);
      onPromoted?.(data.engagement);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    } finally { setPromoting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-3xl max-h-[90vh] overflow-auto" data-testid="proposal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-primary" /> Draft proposal — {opportunity.name}
          </DialogTitle>
        </DialogHeader>

        {!proposal && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">YadidAi (Claude Sonnet 4.5) will draft an engagement scope from this opportunity + linked client context.</p>
            <div>
              <label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Extra context (optional)</label>
              <Textarea rows={4} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Any additional context you want the AI to consider — RFP requirements, competitive dynamics, timing constraints…" className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <Button data-testid="proposal-draft-btn" onClick={draft} disabled={busy} className="rounded-none bg-primary text-primary-foreground w-full h-11">
              <Sparkle size={14} weight="fill" className="mr-2" /> {busy ? "Drafting proposal…" : "Draft proposal with YadidAi"}
            </Button>
          </div>
        )}

        {proposal && (
          <div className="space-y-5 text-sm">
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">{proposal.engagement_type} · {proposal.billing_model}</div>
              <h2 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="proposal-title">{proposal.name}</h2>
              <p className="text-muted-foreground mt-2">{proposal.executive_summary}</p>
            </div>

            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Situation</div>
              <p className="leading-relaxed whitespace-pre-wrap">{proposal.situation}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Objectives</div>
                <ul className="space-y-1">{(proposal.objectives || []).map((o, i) => <li key={i} className="pl-3 border-l-2 border-primary/40">{o}</li>)}</ul>
              </div>
              <div>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Key risks</div>
                <ul className="space-y-1">{(proposal.key_risks || []).map((o, i) => <li key={i} className="pl-3 border-l-2 border-destructive/40">{o}</li>)}</ul>
              </div>
            </div>

            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Scope</div>
              <p className="leading-relaxed">{proposal.scope_summary}</p>
            </div>

            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Phases</div>
              <div className="space-y-2">
                {(proposal.phases || []).map((p, i) => (
                  <div key={i} className="border border-border p-3">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{i + 1}. {p.name}</div>
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">{p.duration_weeks}w</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{p.focus}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Deliverables</div>
              <div className="space-y-2">
                {(proposal.deliverables || []).map((d, i) => (
                  <div key={i} className="border-l-2 border-primary/50 pl-3">
                    <div className="font-medium">{d.title}</div>
                    <div className="text-xs text-muted-foreground">{d.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="border border-border bg-card p-3">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Timeline</div>
                <div className="font-display text-lg font-bold mt-1 data-cell">{proposal.timeline_weeks || "?"}w</div>
              </div>
              <div className="border border-border bg-card p-3">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Price range</div>
                <div className="font-display text-lg font-bold text-primary mt-1 data-cell">{fmt$(proposal.price_low)} – {fmt$(proposal.price_high)}</div>
              </div>
              <div className="border border-border bg-card p-3">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Team</div>
                <div className="text-xs mt-1 leading-tight">{proposal.team_composition}</div>
              </div>
            </div>

            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Why us</div>
              <p className="text-sm">{proposal.why_us}</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {proposal && (
            <>
              <Button onClick={draft} disabled={busy} variant="outline" className="rounded-none" data-testid="proposal-redraft">
                <Sparkle size={12} className="mr-1" /> {busy ? "Redrafting…" : "Redraft"}
              </Button>
              <Button onClick={promote} disabled={promoting} className="rounded-none bg-primary text-primary-foreground" data-testid="proposal-promote">
                {promoting ? "Promoting…" : (<>Promote to engagement <ArrowRight size={14} className="ml-2" /></>)}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
